(() => {
  const types = {
    steeldarts: 'barrel', softdarts: 'barrel', 'steel tip darts': 'barrel', 'soft tip darts': 'barrel',
    'shaft x flights': 'integrated', 'flightshaft-systeme': 'integrated', shafts: 'shaft', flights: 'flight',
    spitzen: 'points', case: 'case', dartcase: 'case', 'steeldart-boards': 'board', 'softdart-boards': 'board',
    dartboards: 'board', surround: 'surround', zahlenring: 'number_ring', 'dartboard beleuchtung': 'lighting',
    'auto scoring': 'scoring'
  };
  const groups = Object.freeze({
    darts: ['barrel'], dart_accessories: ['integrated', 'shaft', 'flight', 'points', 'soft_points', 'conversion_points'],
    board_accessories: ['surround', 'number_ring', 'lighting', 'scoring'], dartboards: ['board'], cases: ['case']
  });
  const list = value => Array.isArray(value) ? value : [];
  const same = (a, b) => typeof a === 'string' && a.length > 0 && a === b;
  const lower = value => String(value || '').trim().toLowerCase();
  const includes = (values, value) => !!value && list(values).includes(value);
  const overlaps = (a, b) => list(a).some(value => includes(b, value));
  const available = p => p.available === true && p.price > 0 && !p.giftCard && !p.subscriptionOnly;

  function details(product) {
    const existing = product.profile || {}, extra = product.recommendation || {};
    const title = lower(product.title), type = lower(product.type), brand = lower(product.vendor || list(product.tags)[0]);
    const p = { ...existing, category: existing.category || types[type] || '', brand };
    if (!p.category && /winmau.*plasma.*(light|beleuchtung)/.test(title)) p.category = 'lighting';
    if (!p.category && /dartständer für 3 darts/.test(title)) p.category = 'case';
    if (p.category === 'barrel') {
      if (type === 'steeldarts' || type === 'steel tip darts') p.dartKind = 'steel';
      if (type === 'softdarts' || type === 'soft tip darts') p.dartKind = 'soft';
    }
    if (p.category === 'board') {
      if (type === 'steeldart-boards') p.boardKind = 'steel';
      if (type === 'softdart-boards') p.boardKind = 'electronic';
      if (/winmau.*blade 6\b/.test(title)) {
        p.boardStandard = 'standard_bristle';
        p.boardFamily = /triple core/.test(title) ? 'blade6_triple' : /dual core/.test(title) ? 'blade6_dual' : 'blade6';
      }
      if (/precise\s*180.*endorphine/.test(title)) {
        p.boardStandard = 'standard_bristle';
        p.boardFamily = 'precise_endorphine';
      }
    }
    if (p.category === 'surround') {
      if (/winmau/.test(title)) p.compatibleBoardBrands = ['winmau', 'red dragon', 'nodor'];
      if (/precise\s*180.*endorphine/.test(title)) p.compatibleBoardStandards = ['standard_bristle'];
    }
    if (p.category === 'lighting') {
      if (/winmau.*plasma/.test(title)) {
        p.system = 'winmau_plasma'; p.universalBoardLight = true;
      }
      if (/target.*corona vision/.test(title)) {
        p.system = 'target_corona'; p.requiresMagneticBand = true;
      }
    }
    if (p.category === 'scoring' && /scolia home 2\b/.test(title)) {
      p.system = 'scolia_home2';
      p.compatibleBoardStandards = ['standard_bristle'];
      p.requiredLighting = ['winmau_plasma', 'scolia_home2_light'];
      if (/inklusive beleuchtung|inkl\. beleuchtung|including light/.test(title)) p.includes = ['lighting'];
    }
    if (p.category === 'number_ring' && /precise\s*180.*onepiece/.test(title)) {
      p.compatibleBoardFamilies = ['precise_endorphine', 'blade6_dual', 'blade6_triple', 'unicorn_eclipse_ultra'];
    }
    if (p.category === 'case') {
      if (/target.*takoma/.test(title)) p.storageCategories = ['barrel', 'integrated', 'shaft', 'flight', 'points', 'soft_points'];
      if (/harrows.*vault/.test(title)) p.storageCategories = ['barrel', 'integrated', 'shaft', 'flight'];
      if (/winmau.*armor g2/.test(title)) p.storageCategories = ['barrel', 'shaft', 'flight'];
      if (/l-style.*flight case/.test(title)) { p.storageCategories = ['flight']; p.storageFlightSystems = ['lstyle_champagne']; }
      if (/dartständer für 3 darts/.test(title)) p.storageCategories = ['barrel'];
    }
    Object.assign(p, extra);
    for (const key of ['pointSystem', 'shaftThread', 'flightSystem', 'dartKind']) {
      if (includes(existing.provisionalFields, key) || ['conflicting', 'unknown', 'unverified'].includes(existing.fieldStatus?.[key])) {
        if (!Object.hasOwn(extra, key)) delete p[key];
      }
    }
    if (p.category === 'points' && p.dartKind === 'soft') p.category = 'soft_points';
    return p;
  }

  const category = product => details(product).category;
  const pointCategory = c => ['points', 'soft_points', 'conversion_points'].includes(c);
  const pointBucket = c => pointCategory(c) ? 'points' : c;

  function pointFits(dart, point) {
    if (!same(dart.pointSystem, point.pointSystem)) return false;
    if (point.category === 'points') return dart.dartKind === 'steel';
    if (point.category === 'soft_points') return dart.dartKind === 'soft' || ['evo', 'ezevo', 'convertible'].includes(point.pointSystem);
    return point.category === 'conversion_points' && dart.dartKind === 'soft' && dart.pointSystem === '2ba_front';
  }

  function storageFits(storage, item) {
    if (!includes(storage.storageCategories, item.category)) return false;
    if (list(storage.storageFlightSystems).length && !includes(storage.storageFlightSystems, item.flightSystem)) return false;
    if (list(storage.storagePointSystems).length && !includes(storage.storagePointSystems, item.pointSystem)) return false;
    return true;
  }

  function boardFits(board, accessory) {
    if (board.category !== 'board' || includes(board.includes, accessory.category)) return false;
    if (accessory.category === 'scoring' && board.boardKind !== 'steel') return false;
    if (accessory.requiresMagneticBand === true && board.magneticBand !== true) return false;
    if (Number.isFinite(accessory.minBoardDiameter) && (!Number.isFinite(board.boardDiameter) || board.boardDiameter < accessory.minBoardDiameter)) return false;
    if (Number.isFinite(accessory.maxBoardDiameter) && (!Number.isFinite(board.boardDiameter) || board.boardDiameter > accessory.maxBoardDiameter)) return false;
    if (list(accessory.compatibleBoardKinds).length && !includes(accessory.compatibleBoardKinds, board.boardKind)) return false;
    if (accessory.numberRingMount && !same(accessory.numberRingMount, board.numberRingMount)) return false;
    if (list(accessory.acceptedNumberRings).length && !includes(accessory.acceptedNumberRings, board.numberRing)) return false;
    return accessory.universalBoardLight === true ||
      (accessory.requiresMagneticBand === true && board.magneticBand === true) ||
      includes(accessory.compatibleBoardFamilies, board.boardFamily) ||
      (board.boardKind === 'steel' && includes(accessory.compatibleBoardBrands, board.brand)) ||
      includes(accessory.compatibleBoardStandards, board.boardStandard);
  }

  function accessoriesFit(a, b) {
    if (a.category === b.category || includes(a.includes, b.category) || includes(b.includes, a.category)) return false;
    if (a.category === 'scoring' || b.category === 'scoring') {
      const scoring = a.category === 'scoring' ? a : b, other = scoring === a ? b : a;
      if (other.category === 'lighting') return includes(scoring.requiredLighting, other.system) && !includes(scoring.includes, 'lighting');
      return includes(scoring.compatibleAccessories, other.system) || includes(other.compatibleScoringSystems, scoring.system);
    }
    if (a.category === 'lighting' && a.universalBoardLight === true && b.category === 'surround') return true;
    if (b.category === 'lighting' && b.universalBoardLight === true && a.category === 'surround') return true;
    return includes(a.compatibleAccessories, b.system) || includes(b.compatibleAccessories, a.system);
  }

  function hasShaft(dart, flight, catalog) {
    return catalog.some(p => {
      const shaft = details(p);
      return available(p) && shaft.category === 'shaft' && same(dart.shaftThread, shaft.shaftThread) && same(flight.flightSystem, shaft.flightSystem);
    });
  }

  function matches(subject, candidate, catalog = []) {
    if (!available(candidate) || subject.giftCard || String(subject.id) === String(candidate.id)) return false;
    const a = details(subject), b = details(candidate);
    if (a.disabled === true || b.disabled === true || !a.category || !b.category || includes(a.includes, b.category)) return false;
    if (a.category === 'barrel') {
      if (['integrated', 'shaft'].includes(b.category)) return same(a.shaftThread, b.shaftThread);
      if (b.category === 'flight') return hasShaft(a, b, catalog);
      if (pointCategory(b.category)) return pointFits(a, b);
      if (b.category === 'case') return storageFits(b, a);
      return b.category === 'tool' && same(a.pointSystem, b.toolSystem || b.pointSystem);
    }
    if (a.category === 'integrated') {
      return b.category === 'barrel' ? same(a.shaftThread, b.shaftThread) : b.category === 'case' && storageFits(b, a);
    }
    if (a.category === 'shaft') {
      if (b.category === 'flight') return same(a.flightSystem, b.flightSystem);
      if (b.category === 'barrel') return same(a.shaftThread, b.shaftThread);
      return b.category === 'case' && storageFits(b, a);
    }
    if (a.category === 'flight') {
      if (b.category === 'shaft') return same(a.flightSystem, b.flightSystem);
      if (b.category === 'barrel') return hasShaft(b, a, catalog);
      if (b.category === 'ring') return a.requiresRing === true && same(a.flightSystem, b.flightSystem);
      return b.category === 'case' && storageFits(b, a);
    }
    if (pointCategory(a.category)) {
      if (b.category === 'barrel') return pointFits(b, a);
      if (b.category === 'tool') return same(a.pointSystem, b.toolSystem || b.pointSystem);
      return b.category === 'case' && storageFits(b, a);
    }
    if (a.category === 'board') return boardFits(a, b);
    if (groups.board_accessories.includes(a.category)) {
      return b.category === 'board' ? boardFits(b, a) : groups.board_accessories.includes(b.category) && accessoriesFit(a, b);
    }
    if (a.category === 'case') return storageFits(a, b);
    if (a.category === 'ring') return b.category === 'flight' && b.requiresRing === true && same(a.flightSystem, b.flightSystem);
    if (a.category === 'tool') {
      if (b.category === 'barrel') return same(a.toolSystem || a.pointSystem, b.pointSystem);
      return pointCategory(b.category) && same(a.toolSystem || a.pointSystem, b.pointSystem);
    }
    return false;
  }

  function plan(subject) {
    const p = details(subject), type = p.category;
    if (type === 'barrel') return { primary: ['integrated', 'shaft', 'flight', 'points'], fallback: ['case', 'tool'] };
    if (type === 'integrated') return { primary: ['barrel', 'barrel', 'barrel', 'case'], fallback: [] };
    if (type === 'shaft') return { primary: ['flight', 'barrel'], fallback: ['case'] };
    if (type === 'flight') return { primary: ['shaft', 'barrel'], fallback: ['ring', 'case'] };
    if (pointCategory(type)) return { primary: ['barrel'], fallback: ['tool', 'case'] };
    if (type === 'board') return { primary: ['surround', 'number_ring', 'lighting', 'scoring'], fallback: [] };
    if (type === 'scoring') return { primary: ['board', 'board', 'board', 'surround'], fallback: ['lighting', 'number_ring'] };
    if (['surround', 'lighting', 'number_ring'].includes(type)) return { primary: ['board', ...groups.board_accessories.filter(c => c !== type)], fallback: [] };
    if (type === 'case') return { primary: ['barrel', 'integrated', 'shaft', 'flight', 'points'], fallback: [] };
    if (type === 'ring') return { primary: ['flight'], fallback: [] };
    if (type === 'tool') return { primary: ['barrel', 'points'], fallback: [] };
    return { primary: [], fallback: [] };
  }

  function select(subject, ranked, limit, excluded = []) {
    const seen = new Set([String(subject.id), ...excluded.map(String)]), queues = new Map(), output = [];
    for (const product of ranked) {
      const id = String(product.id);
      if (seen.has(id)) continue;
      seen.add(id);
      if (!matches(subject, product, ranked)) continue;
      const group = pointBucket(category(product));
      if (!queues.has(group)) queues.set(group, []);
      queues.get(group).push(product);
    }
    const take = slots => {
      let added = true;
      while (added && output.length < limit) {
        added = false;
        for (const slot of slots) {
          const product = queues.get(slot)?.shift();
          if (product) { output.push(product); added = true; }
          if (output.length >= limit) break;
        }
      }
    };
    const route = plan(subject);
    take(route.primary); take(route.fallback);
    return output;
  }

  window.LFDBestsellerRules = Object.freeze({ details, category, groups, available, matches, plan, select });
})();
